// TrailEditSheet
// ==============
//
// Wave 14ca-fix39: In-App-Trail-Editor fuer Admins. Wird von der MapPage
// geoeffnet wenn Admin auf "Edit" tippt — Bottom-Sheet via Portal, damit
// kein Stacking-Context-Konflikt mit den Map-Buttons (selber Trick wie
// im DifficultyFilter).
//
// Spiegelt die Felder des Trail-Editors aus apps/admin/src/App.tsx:
//   - Name (text)
//   - Type (downhill/uphill/flow/connector/singletrack/both)
//   - Difficulty (uphill/easy/intermediate/intermediate_plus/hard/expert/extreme/unknown)
//   - Color override (checkbox + color picker, default = aus Difficulty)
//   - Reverse direction (eigener Button, separates Backend-Endpoint)
//
// Auth: braucht den lokalen useAdminToken — derselbe Token den auch
// AddCruxHereButton + appendCruxPoint nutzen. Ohne Token rendert das Sheet
// gar nicht (Caller in MapPage prueft).

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  DIFFICULTY_COLOR,
  DIFFICULTY_LABEL,
  DIFFICULTY_ORDER,
  type Difficulty,
  type TrailType,
} from '@adtu/shared';
import {
  reverseTrailDirection,
  updateTrailMeta,
} from '../lib/admin';
import { useAdminToken } from '../lib/useUiSettings';

const TRAIL_TYPES: Array<{ value: TrailType; label: string }> = [
  { value: 'downhill', label: 'Downhill' },
  { value: 'uphill', label: 'Uphill (pedal path)' },
  { value: 'flow', label: 'Flow' },
  { value: 'connector', label: 'Connector' },
  { value: 'singletrack', label: 'Singletrack' },
  { value: 'both', label: 'Both directions' },
];

export interface TrailEditSheetProps {
  trail: {
    id: string;
    name: string;
    trailType: TrailType;
    difficulty: Difficulty;
    colorHex?: string | null;
  };
  onClose: () => void;
  /** Callback nach erfolgreichem Save oder Reverse — Caller laedt den
      Trail neu (refresh trails-API + Map-Repaint). */
  onSaved: () => void;
}

export function TrailEditSheet(props: TrailEditSheetProps) {
  return createPortal(<Sheet {...props} />, document.body);
}

function Sheet({ trail, onClose, onSaved }: TrailEditSheetProps) {
  const [adminToken] = useAdminToken();

  const [name, setName] = useState(trail.name);
  const [trailType, setTrailType] = useState<TrailType>(trail.trailType);
  const [difficulty, setDifficulty] = useState<Difficulty>(trail.difficulty);
  const [useColorOverride, setUseColorOverride] = useState(
    Boolean(trail.colorHex),
  );
  const [colorOverride, setColorOverride] = useState<string>(
    trail.colorHex || DIFFICULTY_COLOR[trail.difficulty] || '#1565c0',
  );

  const [busy, setBusy] = useState(false);
  const [reverseBusy, setReverseBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  // ESC schliesst
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy && !reverseBusy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, busy, reverseBusy]);

  const effectiveColor = useColorOverride
    ? colorOverride
    : DIFFICULTY_COLOR[difficulty] || '#1565c0';

  const handleSave = async () => {
    if (!adminToken) {
      setError('Admin token missing — set it in Profile.');
      return;
    }
    setBusy(true);
    setError(null);
    setInfo(null);
    const result = await updateTrailMeta(trail.id, adminToken, {
      name: name.trim(),
      trailType,
      difficulty,
      colorHex: useColorOverride ? colorOverride : null,
    });
    setBusy(false);
    if (result.ok) {
      setInfo('Saved.');
      onSaved();
      // Kurzes Feedback dann schliessen.
      setTimeout(() => onClose(), 600);
    } else {
      setError(`Save failed: ${result.error}`);
    }
  };

  const handleReverse = async () => {
    if (!adminToken) {
      setError('Admin token missing — set it in Profile.');
      return;
    }
    const ok = window.confirm(
      `Reverse direction of "${trail.name}"?\n\nThis flips the GPX track so start ↔ finish are swapped. Affects all riders.`,
    );
    if (!ok) return;
    setReverseBusy(true);
    setError(null);
    setInfo(null);
    const result = await reverseTrailDirection(trail.id, adminToken);
    setReverseBusy(false);
    if (result.ok) {
      setInfo('Direction reversed.');
      onSaved();
    } else {
      setError(`Reverse failed: ${result.error}`);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-end justify-center bg-black/60">
      <div className="w-full max-w-md max-h-[85vh] overflow-y-auto bg-turbo-900 border-t border-turbo-500/30 rounded-t-2xl p-5 pb-8 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-white">
            Edit trail{' '}
            <span className="text-white/55 font-normal">· {trail.name}</span>
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={busy || reverseBusy}
            className="text-white/55 hover:text-white text-2xl leading-none disabled:opacity-40"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* Color preview swatch */}
        <div className="flex items-center gap-2 mb-4">
          <div
            className="w-8 h-8 rounded-md border border-white/20 shrink-0"
            style={{ background: effectiveColor }}
          />
          <span className="text-xs text-white/55 font-mono">
            {effectiveColor}
          </span>
        </div>

        {error && (
          <div className="mb-3 bg-red-500/15 border border-red-500/40 rounded-lg p-2 text-red-200 text-xs">
            {error}
          </div>
        )}
        {info && (
          <div className="mb-3 bg-emerald-500/15 border border-emerald-500/40 rounded-lg p-2 text-emerald-200 text-xs">
            {info}
          </div>
        )}

        <div className="space-y-3">
          <Field label="Name">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={busy}
              className="w-full bg-turbo-700/40 border border-turbo-500/30 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-sky-500/60"
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Type">
              <select
                value={trailType}
                onChange={(e) => setTrailType(e.target.value as TrailType)}
                disabled={busy}
                className="w-full bg-turbo-700/40 border border-turbo-500/30 rounded-lg px-2 py-2 text-white text-sm focus:outline-none focus:border-sky-500/60"
              >
                {TRAIL_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Difficulty">
              <select
                value={difficulty}
                onChange={(e) => {
                  const d = e.target.value as Difficulty;
                  setDifficulty(d);
                  // Color-Override leer: passe Vorschau-Farbe an
                  if (!useColorOverride) {
                    setColorOverride(DIFFICULTY_COLOR[d] || '#1565c0');
                  }
                }}
                disabled={busy}
                className="w-full bg-turbo-700/40 border border-turbo-500/30 rounded-lg px-2 py-2 text-white text-sm focus:outline-none focus:border-sky-500/60"
              >
                {DIFFICULTY_ORDER.map((d) => (
                  <option key={d} value={d}>
                    {DIFFICULTY_LABEL[d]}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <label className="flex items-center gap-2 text-sm text-white/80 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={useColorOverride}
              onChange={(e) => {
                setUseColorOverride(e.target.checked);
                if (e.target.checked && !colorOverride) {
                  setColorOverride(DIFFICULTY_COLOR[difficulty] || '#1565c0');
                }
              }}
              disabled={busy}
              className="w-4 h-4"
            />
            Override color (otherwise from difficulty)
          </label>

          {useColorOverride && (
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={colorOverride || '#1565c0'}
                onChange={(e) => setColorOverride(e.target.value)}
                disabled={busy}
                className="w-12 h-10 rounded cursor-pointer disabled:opacity-50"
              />
              <input
                type="text"
                value={colorOverride}
                onChange={(e) => setColorOverride(e.target.value)}
                disabled={busy}
                placeholder="#rrggbb"
                className="flex-1 bg-turbo-700/40 border border-turbo-500/30 rounded-lg px-3 py-2 text-white text-sm font-mono focus:outline-none focus:border-sky-500/60"
              />
            </div>
          )}
        </div>

        <div className="mt-5 pt-4 border-t border-turbo-700/40">
          <button
            type="button"
            onClick={handleReverse}
            disabled={busy || reverseBusy}
            className="w-full text-sm text-amber-300 hover:text-amber-200 disabled:text-white/30 disabled:cursor-not-allowed py-2 rounded-lg border border-amber-500/30 hover:border-amber-500/50 disabled:border-turbo-500/20 transition-colors"
          >
            {reverseBusy ? 'Reversing…' : '↻ Reverse track direction'}
          </button>
          <p className="text-[11px] text-white/40 mt-1 leading-tight">
            Flip start ↔ finish (use if track was recorded backwards).
          </p>
        </div>

        <div className="mt-5 flex gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy || reverseBusy}
            className="flex-1 bg-turbo-700/40 border border-turbo-500/30 rounded-lg py-2.5 text-white text-sm font-medium hover:bg-turbo-700/60 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={busy || reverseBusy || !name.trim()}
            className="flex-1 bg-sky-500 hover:bg-sky-400 active:bg-sky-600 rounded-lg py-2.5 text-white text-sm font-semibold disabled:bg-turbo-700/40 disabled:text-white/40 disabled:cursor-not-allowed"
          >
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="block text-white/80 text-xs font-medium mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}
